import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Box, Link, useTheme } from "@mui/material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  INVENTORY_ADJUSTMENT_CSV_HEADERS,
  INVENTORY_ADJUSTMENT_MAX_LINES,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";
import InventoryAdjustmentDialogShell from "./InventoryAdjustmentDialogShell";
import {
  AdjustmentReasonField,
  InlineFormSuccess,
} from "./InventoryAdjustmentParts";

/** Header-only template so no sample rows get imported by mistake. */
export const downloadInventoryAdjustmentTemplate = () => {
  try {
    const csv = `${INVENTORY_ADJUSTMENT_CSV_HEADERS.join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SwageazyInventory_Adjustment_Template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
  }
};

interface SwageazyInventoryAdjustCsvPopupProps {
  store: SwageazyInventoryAdjustmentStore;
  onSaved: () => void;
}

const SwageazyInventoryAdjustCsvPopup: React.FC<
  SwageazyInventoryAdjustCsvPopupProps
> = ({ store, onSaved }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = async () => {
    try {
      const result = await store.uploadCsv();
      if (result) onSaved();
    } catch (error) {
      console.error(error);
    }
  };

  const result = store.csvResult;

  return (
    <InventoryAdjustmentDialogShell
      open={store.isCsvOpen}
      title="Upload Adjustment CSV"
      subtitle={`Up to ${INVENTORY_ADJUSTMENT_MAX_LINES} rows, 1 MB. Every row is validated first; if any row fails, nothing is saved.`}
      onClose={store.closeCsv}
      disableClose={store.isCsvUploading}
      footer={
        <>
          <AppButton
            variant="transparentPrimary"
            label="Close"
            onClick={store.closeCsv}
            disabled={store.isCsvUploading}
          />
          <AppButton
            variant="primary"
            label={store.isCsvUploading ? "Uploading..." : "Upload"}
            onClick={handleUpload}
            disabled={store.isCsvUploading || !store.csvFile}
          />
        </>
      }
    >
      <Box sx={modernSectionPaperSx(theme)}>
        <CustomTypography variant="heading8" color={colors.label}>
          Columns
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={colors.title}
          sx={{ mt: 0.5, fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {INVENTORY_ADJUSTMENT_CSV_HEADERS.join(",")}
        </CustomTypography>
        <CustomTypography
          variant="caption"
          color={colors.subtitle}
          sx={{ mt: 0.75, display: "block" }}
        >
          Quantity is signed: positive adds, negative removes. Box must already
          exist in the warehouse (e.g. B00001). expiry_date (YYYY-MM-DD) and
          note are optional; expiry is required when adding to a product that
          tracks expiry.{" "}
          <Link
            component="button"
            type="button"
            onClick={downloadInventoryAdjustmentTemplate}
            sx={{ color: colors.accent, fontWeight: 600, fontSize: "inherit" }}
          >
            Download template
          </Link>
        </CustomTypography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          p: 2,
          border: `1px dashed ${colors.border}`,
          borderRadius: 2,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => {
            store.setCsvFile(e.target.files?.[0] || null);
            e.target.value = "";
          }}
        />
        <AppButton
          variant="grayscale"
          label="Choose CSV"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={store.isCsvUploading}
        />
        <CustomTypography variant="body2" color={colors.subtitle}>
          {store.csvFile ? store.csvFile.name : "No file selected"}
        </CustomTypography>
      </Box>

      <AdjustmentReasonField
        value={store.csvReason}
        onChange={store.setCsvReason}
        disabled={store.isCsvUploading}
      />

      <InlineFormError message={store.csvError} sx={{ mt: 0, mb: 0 }} />
      {store.csvRowErrors.length > 0 && (
        <Box
          sx={{
            maxHeight: 220,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {store.csvRowErrors.map((rowError, index) => (
            <InlineFormError
              key={`${rowError.row}-${index}`}
              message={rowError.message}
              sx={{ mt: 0, mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {result && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <InlineFormSuccess
            message={`Adjustment #${result.adjustment_id} imported: ${result.line_count} line(s), +${result.total_added} added, −${result.total_removed} removed.`}
          />
          {result.sheet_url && (
            <Link
              href={result.sheet_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: colors.accent, fontSize: "0.8rem", fontWeight: 600 }}
            >
              View uploaded sheet
            </Link>
          )}
        </Box>
      )}
    </InventoryAdjustmentDialogShell>
  );
};

export default observer(SwageazyInventoryAdjustCsvPopup);
